import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAddressSuggestions,
  formatGroundCoverageSqMi,
  MIN_GROUND_SQ_MI,
  MAX_GROUND_SQ_MI,
} from '../../services/googleMapsSite';
import { mToFt } from '../../utils/unitsUs';
import {
  CONSTRUX_CATALOG,
  DEFAULT_SITE_PLANNER_MODEL,
  catalogLabel,
  catalogOrigin,
  softplanExamplesCatalog,
} from '../../data/sitePlannerCatalog';
import { scaleLotVertices2d } from '../../utils/plotGeom';

/**
 * Stage-scoped floating sheet for ADU site planner (Roomle-style chrome).
 * Docs: docs/ADU_SITE_PLANNER.md · docs/VIEWER_UX_BRIEF.md
 */
function SitePlannerPanel({
  stage = 'site',
  address,
  onAddressChange,
  onLoadSite,
  siteLoading,
  siteError,
  site,
  groundSqMi,
  onGroundSqMiChange,
  lot,
  onLotChange,
  onResetLot,
  onResetLotFloor,
  floorDebug = null,
  floorY = null,
  siteContentReady = true,
  viewLocked,
  onViewLockedChange,
  lotSelected,
  onSelectLot,
  mapBuildingCount = 0,
  contextMode = 'google',
  onContextModeChange,
  structures,
  selectedId,
  onSelectStructure,
  onAddStructure,
  onRemoveStructure,
  layers,
  onLayersChange,
  section,
  onSectionChange,
  liveSyncEnabled = false,
  onLiveSyncEnabledChange,
  liveSyncStatus = null,
  onLiveSyncNow,
  onRecaptureContext,
  liveSyncBusy = false,
  captureTilesEnabled = false,
  liveModels = [],
  liveModelId = '',
  onLiveModelChange,
  onRefreshLiveModels,
  onCreateLiveModel,
  liveProjectUrl = null,
  catalogType: catalogTypeProp,
  onCatalogTypeChange,
}) {
  const selected = structures.find((s) => s.id === selectedId);
  const [catalogTypeLocal, setCatalogTypeLocal] = useState(
    DEFAULT_SITE_PLANNER_MODEL
  );
  const catalogType = catalogTypeProp ?? catalogTypeLocal;
  const setCatalogType = onCatalogTypeChange ?? setCatalogTypeLocal;
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [draftSqMi, setDraftSqMi] = useState(groundSqMi);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const skipSuggestRef = useRef(false);

  const showSite = stage === 'site';
  const showPlace = stage === 'place';
  const showInspect = stage === 'inspect';
  const showNext = stage === 'next';
  const showTools = stage === 'tools';

  useEffect(() => {
    setDraftSqMi(groundSqMi);
  }, [groundSqMi]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setSuggestOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (skipSuggestRef.current) {
      skipSuggestRef.current = false;
      return undefined;
    }
    const q = address.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSuggestOpen(false);
      setSuggestError(null);
      return undefined;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await fetchAddressSuggestions(q);
        setSuggestions(list);
        setSuggestOpen(list.length > 0);
        setActiveIndex(-1);
        setSuggestError(null);
      } catch (err) {
        setSuggestions([]);
        setSuggestOpen(false);
        setSuggestError(err?.message || String(err));
      }
    }, 280);

    return () => clearTimeout(debounceRef.current);
  }, [address]);

  const pickSuggestion = useCallback(
    (item) => {
      skipSuggestRef.current = true;
      onAddressChange(item.description);
      setSuggestions([]);
      setSuggestOpen(false);
      setActiveIndex(-1);
    },
    [onAddressChange]
  );

  const onAddressKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (suggestOpen && activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        pickSuggestion(suggestions[activeIndex]);
        return;
      }
      setSuggestOpen(false);
      onLoadSite();
      return;
    }
    if (!suggestOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setSuggestOpen(false);
      setActiveIndex(-1);
    }
  };

  const stageTitle =
    {
      site: 'Site',
      place: 'Place',
      inspect: 'Inspect',
      next: 'Next',
      tools: 'Tools',
    }[stage] || 'Controls';

  return (
    <aside className="w-full h-full max-h-[inherit] border border-black/8 bg-white/95 backdrop-blur-md rounded-[10px] p-3.5 pr-11 font-bai text-sm flex flex-col gap-3.5 shadow-lg overflow-y-auto">
      <h2 className="text-[10px] uppercase tracking-[0.14em] text-gray-400 font-medium">
        {stageTitle}
      </h2>

      {showSite && (
        <>
          <div>
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="site-address"
            >
              Address
            </label>
            <div className="flex gap-2 items-start">
              <div className="relative flex-1 min-w-0" ref={wrapRef}>
                <input
                  id="site-address"
                  type="text"
                  value={address}
                  onChange={(e) => onAddressChange(e.target.value)}
                  onFocus={() => {
                    if (suggestions.length > 0) setSuggestOpen(true);
                  }}
                  placeholder="Start typing an address…"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={suggestOpen}
                  aria-controls="site-address-suggestions"
                  aria-autocomplete="list"
                  className="w-full border border-warm-200 rounded-[10px] px-2.5 py-2 text-sm bg-white"
                  onKeyDown={onAddressKeyDown}
                />
                {suggestOpen && suggestions.length > 0 && (
                  <ul
                    id="site-address-suggestions"
                    role="listbox"
                    className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto border border-warm-200 bg-white rounded-[10px] shadow-md"
                  >
                    {suggestions.map((item, i) => (
                      <li
                        key={item.placeId}
                        role="option"
                        aria-selected={i === activeIndex}
                      >
                        <button
                          type="button"
                          className={`w-full text-left px-2.5 py-2 text-xs leading-snug ${
                            i === activeIndex
                              ? 'bg-warm-100'
                              : 'hover:bg-warm-50'
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickSuggestion(item)}
                        >
                          {item.description}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                onClick={onLoadSite}
                disabled={siteLoading || !address.trim()}
                className="px-3 py-2 bg-gray-900 text-white rounded-[10px] text-xs font-medium disabled:opacity-40 whitespace-nowrap"
              >
                {siteLoading ? '…' : 'Load'}
              </button>
            </div>
            {suggestError && (
              <p className="mt-2 text-[11px] text-amber-800" role="status">
                Autocomplete unavailable: enable Places API + Maps JavaScript
                API for this key.
              </p>
            )}
            {siteError && (
              <p className="mt-2 text-xs text-red-700" role="alert">
                {siteError}
              </p>
            )}
            {site && (
              <p className="mt-2 text-xs text-gray-500 leading-snug">
                {site.lat.toFixed(5)}, {site.lng.toFixed(5)} ·{' '}
                {formatGroundCoverageSqMi(site.groundSqMi ?? groundSqMi)}
              </p>
            )}
          </div>

          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
              Lot
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-2">
              <button
                type="button"
                onClick={() => onSelectLot?.()}
                disabled={!site || !siteContentReady}
                className={`px-2.5 py-1 text-xs rounded-[10px] border disabled:opacity-40 ${
                  lotSelected
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-warm-200 hover:bg-warm-50'
                }`}
              >
                {lotSelected ? 'Lot selected' : 'Select lot'}
              </button>
              <button
                type="button"
                onClick={() => onViewLockedChange?.(!viewLocked)}
                disabled={!site || !siteContentReady}
                className={`px-2.5 py-1 text-xs rounded-[10px] border disabled:opacity-40 ${
                  viewLocked
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-warm-200 hover:bg-warm-50'
                }`}
              >
                {viewLocked ? 'Unlock view' : 'Lock view'}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1">
              <button
                type="button"
                onClick={() => {
                  if (!lot?.vertices?.length || !onLotChange) return;
                  onLotChange({
                    vertices: scaleLotVertices2d(lot.vertices, 0.9),
                    y: Number(lot.y) || 0,
                  });
                  onSelectLot?.();
                }}
                disabled={!site || !siteContentReady || !lot?.vertices?.length}
                className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 disabled:opacity-40"
                title="Shrink lot footprint 10% (XZ only)"
              >
                Scale −
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!lot?.vertices?.length || !onLotChange) return;
                  onLotChange({
                    vertices: scaleLotVertices2d(lot.vertices, 1.1),
                    y: Number(lot.y) || 0,
                  });
                  onSelectLot?.();
                }}
                disabled={!site || !siteContentReady || !lot?.vertices?.length}
                className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 disabled:opacity-40"
                title="Grow lot footprint 10% (XZ only)"
              >
                Scale +
              </button>
              <button
                type="button"
                onClick={() => onResetLotFloor?.()}
                disabled={!site}
                className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 disabled:opacity-40"
              >
                Reset to floor
              </button>
              <button
                type="button"
                onClick={() => onResetLot?.(18, 22)}
                className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50"
              >
                Reset 59×72 ft
              </button>
            </div>
            {site && contextMode === 'google' && (
              <p className="text-[10px] text-gray-400 tabular-nums leading-snug mt-1">
                {siteContentReady
                  ? `Floor locked · ${floorDebug?.source || 'ray'} · Y=${mToFt(
                      floorY ?? floorDebug?.sampleY ?? 0
                    ).toFixed(1)} ft`
                  : `Floor · waiting…${
                      floorDebug?.waitSec != null
                        ? ` ${floorDebug.waitSec}s`
                        : ''
                    }`}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">
              {lot?.vertices?.length || 0} vertices · Scale −/+ or gizmo scale
              (XZ only) · Localize for top-down edit
            </p>
          </div>

          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
              Context 3D
            </h3>
            <div className="flex flex-col gap-1.5 text-xs text-gray-700">
              {[
                ['google', 'Photorealistic'],
                ['osm', `OSM${site ? ` (${mapBuildingCount})` : ''}`],
                ['off', 'Off'],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="context-3d"
                    checked={contextMode === value}
                    onChange={() => onContextModeChange?.(value)}
                    disabled={!site && value !== 'off'}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
              View coverage
            </h3>
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="ground-coverage"
            >
              {formatGroundCoverageSqMi(draftSqMi)}
            </label>
            <input
              id="ground-coverage"
              type="range"
              min={MIN_GROUND_SQ_MI}
              max={MAX_GROUND_SQ_MI}
              step={0.01}
              value={draftSqMi}
              onChange={(e) => setDraftSqMi(Number(e.target.value))}
              onPointerUp={() => onGroundSqMiChange?.(draftSqMi)}
              onKeyUp={(e) => {
                if (
                  e.key === 'Enter' ||
                  e.key === 'ArrowLeft' ||
                  e.key === 'ArrowRight'
                ) {
                  onGroundSqMiChange?.(draftSqMi);
                }
              }}
              className="w-full"
            />
          </div>
        </>
      )}

      {showPlace && (
        <div>
          <p className="text-[11px] text-gray-500 mb-2 leading-snug">
            Construx SKUs vs SoftPlan public gallery examples. Pick a model, then
            Add.
          </p>
          <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
            Construx
          </h3>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {CONSTRUX_CATALOG.map((m) => (
              <button
                key={m.slug}
                type="button"
                onClick={() => setCatalogType(m.slug)}
                disabled={!site}
                className={`px-2.5 py-1.5 text-xs rounded-[10px] border disabled:opacity-40 ${
                  catalogType === m.slug
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-warm-200 hover:bg-warm-50'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {softplanExamplesCatalog().length > 0 && (
            <>
              <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
                SoftPlan examples
              </h3>
              <select
                value={
                  catalogOrigin(catalogType) === 'softplan' ? catalogType : ''
                }
                onChange={(e) => {
                  if (e.target.value) setCatalogType(e.target.value);
                }}
                disabled={!site}
                className="w-full mb-3 px-2.5 py-2 text-xs border border-warm-200 rounded-[10px] bg-white disabled:opacity-40"
                aria-label="SoftPlan example model"
              >
                <option value="">Select SoftPlan example…</option>
                {softplanExamplesCatalog().map((m) => (
                  <option key={m.slug} value={m.slug}>
                    {m.label}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            type="button"
            onClick={() => onAddStructure?.(catalogType)}
            disabled={!site || !siteContentReady}
            className="w-full px-3 py-2 text-xs font-medium rounded-[10px] bg-gray-900 text-white disabled:opacity-40 mb-3"
          >
            Add{' '}
            {catalogOrigin(catalogType) === 'softplan'
              ? `SP · ${catalogLabel(catalogType)}`
              : catalogLabel(catalogType)}
          </button>
          {structures.length === 0 ? (
            <p className="text-xs text-gray-400">No structures yet.</p>
          ) : (
            <ul className="space-y-1">
              {structures.map((s, i) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => onSelectStructure(s.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-[10px] border text-xs flex justify-between gap-2 ${
                      selectedId === s.id
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-warm-200 hover:bg-warm-50'
                    }`}
                  >
                    <span>
                      {catalogOrigin(s.type) === 'softplan' ? 'SP · ' : ''}
                      {catalogLabel(s.type)} #{i + 1}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={
                        selectedId === s.id ? 'text-white/80' : 'text-red-700'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveStructure(s.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          onRemoveStructure(s.id);
                        }
                      }}
                    >
                      Remove
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {selected && (
            <p className="mt-2 text-[11px] text-gray-500">
              Drag gizmo to move (green = up)
            </p>
          )}
        </div>
      )}

      {showInspect && (
        <>
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
              Layers
            </h3>
            <div className="flex flex-col gap-1.5">
              {[
                ['roof', 'Roof'],
                ['walls', 'Walls'],
                ['interior', 'Interior'],
              ].map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 text-xs text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={layers[key]}
                    onChange={(e) =>
                      onLayersChange({ ...layers, [key]: e.target.checked })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
              Section
            </h3>
            <label className="flex items-center gap-2 text-xs text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={section.enabled}
                onChange={(e) =>
                  onSectionChange({ ...section, enabled: e.target.checked })
                }
              />
              Vertical cut (X)
            </label>
            <input
              type="range"
              min={-8}
              max={8}
              step={0.1}
              disabled={!section.enabled}
              value={section.offset}
              onChange={(e) =>
                onSectionChange({
                  ...section,
                  offset: Number(e.target.value),
                })
              }
              className="w-full disabled:opacity-40"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Offset {mToFt(section.offset).toFixed(1)} ft
            </p>
          </div>
        </>
      )}

      {showNext && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-700 leading-snug">
            {site
              ? `${structures.length} structure${
                  structures.length === 1 ? '' : 's'
                } on ${site.formattedAddress}.`
              : 'Load a site and place an ADU to continue.'}
          </p>
          <Link
            to="/models"
            className="inline-flex justify-center px-3 py-2 text-xs font-medium rounded-[10px] bg-gray-900 text-white hover:bg-gray-800"
          >
            Browse model catalog
          </Link>
          <p className="text-[11px] text-gray-400 leading-snug">
            Proposal / quote handoff lands here next.
          </p>
        </div>
      )}

      {showTools && (
        <>
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
              Live Speckle sync
            </h3>
            <p className="text-[11px] text-gray-400 mb-2 leading-snug">
              Internal — lot + ADUs + Photorealistic context (no remesh).
            </p>
            <label
              className="block text-[11px] text-gray-500 mb-1"
              htmlFor="live-model"
            >
              Target model
            </label>
            <div className="flex gap-1.5 mb-2">
              <select
                id="live-model"
                value={liveModelId || ''}
                onChange={(e) => onLiveModelChange?.(e.target.value)}
                disabled={liveSyncBusy || liveModels.length === 0}
                className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-warm-200 rounded-[10px] bg-white disabled:opacity-40"
              >
                {liveModels.length === 0 ? (
                  <option value="">Loading models…</option>
                ) : (
                  liveModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))
                )}
              </select>
              <button
                type="button"
                onClick={() => onRefreshLiveModels?.()}
                disabled={liveSyncBusy}
                className="px-2 py-1 text-xs border border-warm-200 rounded-[10px] hover:bg-warm-50 disabled:opacity-40"
                title="Refresh model list"
              >
                ↻
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              <button
                type="button"
                onClick={() => onCreateLiveModel?.()}
                disabled={liveSyncBusy}
                className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 disabled:opacity-40"
              >
                New model
              </button>
              {liveProjectUrl && (
                <a
                  href={liveProjectUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 inline-flex items-center"
                >
                  Open project
                </a>
              )}
              {(() => {
                const m = liveModels.find((x) => x.id === liveModelId);
                return m?.url ? (
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 inline-flex items-center"
                  >
                    Open model
                  </a>
                ) : null;
              })()}
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={liveSyncEnabled}
                disabled={!site || !liveModelId}
                onChange={(e) => onLiveSyncEnabledChange?.(e.target.checked)}
              />
              Enable live sync
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              <button
                type="button"
                onClick={() => onLiveSyncNow?.()}
                disabled={!site || !liveModelId || liveSyncBusy}
                className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 disabled:opacity-40"
              >
                {liveSyncBusy ? 'Syncing…' : 'Sync now'}
              </button>
              {captureTilesEnabled && (
                <button
                  type="button"
                  onClick={() => onRecaptureContext?.()}
                  disabled={
                    !site ||
                    !liveModelId ||
                    liveSyncBusy ||
                    contextMode !== 'google'
                  }
                  className="px-2.5 py-1 text-xs rounded-[10px] border border-warm-200 hover:bg-warm-50 disabled:opacity-40"
                >
                  Recapture context
                </button>
              )}
            </div>
            {liveSyncStatus?.error && (
              <p className="text-[11px] text-red-700 leading-snug mb-1">
                {liveSyncStatus.error}
              </p>
            )}
            {liveSyncStatus?.versionUrl && (
              <a
                href={liveSyncStatus.versionUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-sky-800 underline break-all"
              >
                Open Speckle version
              </a>
            )}
            {liveSyncStatus?.info && (
              <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                {liveSyncStatus.info}
              </p>
            )}
          </div>
          <div>
            <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
              View coverage
            </h3>
            <label
              className="block text-xs text-gray-600 mb-1"
              htmlFor="ground-coverage-tools"
            >
              {formatGroundCoverageSqMi(draftSqMi)}
            </label>
            <input
              id="ground-coverage-tools"
              type="range"
              min={MIN_GROUND_SQ_MI}
              max={MAX_GROUND_SQ_MI}
              step={0.01}
              value={draftSqMi}
              onChange={(e) => setDraftSqMi(Number(e.target.value))}
              onPointerUp={() => onGroundSqMiChange?.(draftSqMi)}
              className="w-full"
            />
          </div>
        </>
      )}
    </aside>
  );
}

export default SitePlannerPanel;
